import { APP_VER, DRAWER_MINI_WIDTH, DRAWER_WIDTH } from 'app/core/config';
import { useUiStore } from 'app/core/store_v2/ui';
import { isBackoffice, isClient, isGeneral, isInspector } from 'app/helpers/userRoles';
import { RootState } from 'app/store/rootReducer';
import ArrowLeftIcon from 'app/ui_v2/icons/ArrowLeftIcon';
import MenuHamburgerIcon from 'app/ui_v2/icons/MenuHamburgerIcon';
import clsx from 'clsx';
import { useSelector } from 'react-redux';

import { AppBar, Box, CSSObject, IconButton, styled, SxProps, Theme, useTheme } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';

import { getNavigationConfig } from './Navigation/config';
import Navigation from './Navigation/Navigation';
import UserName from './UserName';

const getStyles = (theme: Theme): { [key: string]: SxProps } => {
  return {
    root: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    bar: {
      position: 'relative',
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      height: theme.spacing(8),
      background: theme.palette.secondary.main,
      '&.isClient': {
        background: theme.palette.primary.main,
      },
      '&.isBackoffice': {
        background: theme.palette.grey[700],
      },
      '&.isInspector': {
        background: '#792727',
      },
      width: '100%',
    },
    backButtonContainer: {
      position: 'absolute',
      left: 0,
      top: 8,
      color: theme.palette.background.paper,
    },
    navigationContainer: {
      display: 'flex',
      flexGrow: 1,
      width: '100%',
      overflow: 'auto',
      position: 'relative',
    },
    userNameHeader: {
      textTransform: 'capitalize',
      zIndex: 2,
      textAlign: 'right',
      padding: theme.spacing(1),
      width: `calc(100% - ${DRAWER_MINI_WIDTH}px)`,
      color: theme.palette.common.white,
      fontSize: '1.4rem',
      fontWeight: 700,
    },
    appVer: {
      position: 'absolute',
      fontSize: '1rem',
      opacity: '0.7',
      top: '64px',
      right: 0,
      width: '60px',
      textAlign: 'right',
    },
  };
};

const openedMixin = (theme: Theme): CSSObject => ({
  width: DRAWER_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${DRAWER_MINI_WIDTH}px + 1px)`,
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

export default function NavbarLayout() {
  const theme = useTheme();
  const styles = getStyles(theme);

  const { user, currentApp } = useSelector((state: RootState) => state.auth);
  const { sidebarState, toggleSidebar } = useUiStore();

  if (!user) {
    return null;
  }

  const isUserInspector = isInspector(user);
  const isUserClient = isClient(user);
  const isUserBackoffice = isBackoffice(user) || isGeneral(user);

  const handleClick = () => {
    toggleSidebar();
  };

  return (
    <Drawer open={sidebarState} variant="permanent">
      <Box sx={styles.root}>
        <AppBar elevation={0} color="transparent" position="static">
          <Box
            sx={styles.bar}
            className={clsx(
              isUserClient && 'isClient',
              isUserInspector && 'isInspector',
              isUserBackoffice && 'isBackoffice'
            )}
          >
            <IconButton
              onClick={() => {
                handleClick();
              }}
              sx={{
                color: theme.palette.common.white,
                width: `calc(${DRAWER_MINI_WIDTH}px + 1px)`,
                height: '100%',
              }}
            >
              {sidebarState ? <ArrowLeftIcon /> : <MenuHamburgerIcon />}
            </IconButton>

            <Box sx={styles.userNameHeader}>
              <UserName />
            </Box>
          </Box>
        </AppBar>

        <Box sx={styles.appVer}>
          v{APP_VER} [{user.id}]
        </Box>

        <Box sx={styles.navigationContainer}>
          <Navigation navigation={getNavigationConfig(user, currentApp)} />
        </Box>
      </Box>
    </Drawer>
  );
}
